import { pb } from '../../lib/pocketbase';
import { AuditWithExpand, SettingsRecord, ReportData, ReportOptions } from './types';
import { generateReportHTML } from './reportTemplate';
import { IstZustandMassnahme as BaseMassnahme } from '../../lib/pocketbase';

export class ReportService {
  static async fetchSettings(): Promise<SettingsRecord | null> {
    try {
      const settingsRecords = await pb.collection('settings').getList<SettingsRecord>(1, 1);
      return settingsRecords.items[0] || null;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw new Error('Failed to fetch settings');
    }
  }

  static async updateSettingsEmailTemplate(template: string): Promise<void> {
    try {
      const settingsRecords = await pb.collection('settings').getList<SettingsRecord>(1, 1);
      const record = settingsRecords.items[0];
      if (record?.id) {
        await pb.collection('settings').update(record.id, { emailTemplate: template });
      } else {
        throw new Error("Kein Settings-Datensatz gefunden.");
      }
    } catch (error) {
      console.error('Error updating settings email template:', error);
      throw new Error('Failed to update email template');
    }
  }

  static async fetchAudits(): Promise<AuditWithExpand[]> {
    try {
      // Note: auditor is a JSON field, not a relation, so it can't be expanded
      // We need to manually fetch auditor data
      const audits = await pb.collection('audits').getFullList<AuditWithExpand>({
        sort: '-created',
        // ✅ Nur existierende Relation-Felder expandieren
        expand: 'dienststelle,kontrollpunkt',
        fields: `
          id,
          auditnummer,
          auditdatum,
          status,
          begleitpersonen,
          auditor,
          dienststelle,
          adresse,
          expand.dienststelle.id,
          expand.dienststelle.name,
          expand.dienststelle.direktion,
          expand.dienststelle.expand.direktion.id,
          expand.dienststelle.expand.direktion.name,
          expand.adresse.adresse,
          expand.adresse.postleitzahl,
          expand.kontrollpunkt.sicherheitssystem
        `,
        $autoCancel: false
      });

      // Collect all auditor IDs
      const auditIds = new Set<string>();
      audits.forEach(a => {
        if (Array.isArray(a.auditor)) {
          a.auditor.forEach(id => {
            if (typeof id === 'string') auditIds.add(id);
          });
        }
      });

      if (auditIds.size > 0) {
        try {
          const filter = Array.from(auditIds).map(id => `id="${id}"`).join(' || ');
          const users = await pb.collection('users').getFullList({
            filter: filter,
            fields: 'id,name,email,avatar,collectionId,collectionName'
          });

          // Map users back to audits
          const userMap = new Map(users.map(u => [u.id, u]));

          audits.forEach(audit => {
            if (Array.isArray(audit.auditor)) {
              const auditAuditors = audit.auditor
                .map(id => userMap.get(id as string))
                .filter(Boolean);

              if (auditAuditors.length > 0) {
                if (!audit.expand) audit.expand = {} as any;
                if (audit.expand) {
                  audit.expand.auditor = auditAuditors as any[];
                }
              }
            }
          });
        } catch (err) {
          console.error("Error fetching auditors for audits list:", err);
          // Continue without auditors if fetching fails
        }
      }

      return audits;
    } catch (error) {
      console.error('Error fetching audits:', error);
      throw new Error('Failed to fetch audits');
    }
  }

  static async fetchAuditDetails(auditId: string): Promise<ReportData> {
    try {
       const auditPromise = pb.collection('audits').getOne<AuditWithExpand>(auditId, {
          // ✅ Nur existierende Relations expandieren
          expand: 'dienststelle,kontrollpunkt',
          fields: `
            id,
            auditnummer,
            auditdatum,
            status,
            begleitpersonen,
            adresse,
            auditor,
            dienststelle,
            kontrollpunkt,
            expand.dienststelle.id,
            expand.dienststelle.name,
            expand.dienststelle.direktion,
            expand.dienststelle.expand.direktion.id,
            expand.dienststelle.expand.direktion.name,
            expand.kontrollpunkt.id,
            expand.kontrollpunkt.sicherheitssystem
          `
        });

       const [audit, massnahmen, settings, previousAudit] = await Promise.all([
        auditPromise,
        pb.collection('ist_zustand_massnahmen').getFullList<BaseMassnahme>({
          filter: `audit = "${auditId}"`,
          sort: 'reihenfolge'
        }),
        this.fetchSettings(),
        (async () => {
          try {
            const baseAudit = await auditPromise;
            if (!baseAudit?.dienststelle) return null;
            return await pb.collection('audits').getFirstListItem<AuditWithExpand>(
              `dienststelle = "${baseAudit.dienststelle}" && id != "${baseAudit.id}"`,
              {
                sort: '-auditdatum',
                fields: 'id,auditdatum'
              }
            );
          } catch (err) {
            console.warn('Konnte vorheriges Audit nicht ermitteln:', err);
            return null;
          }
        })()
       ]);

      // Manually fetch auditor data since auditor is a JSON field, not a relation
      if (Array.isArray(audit.auditor) && audit.auditor.length > 0) {
        try {
          const auditorFilter = audit.auditor.map(id => `id="${id}"`).join(' || ');
          const auditorsResult = await pb.collection('users').getList(1, 100, {
            filter: auditorFilter,
            fields: 'id,name,email,avatar,collectionId,collectionName'
          });
          
          // Populate expand.auditor with the fetched data
          if (audit.expand) {
            audit.expand.auditor = auditorsResult.items as any[];
          }
          
          console.log('Fetched audit data:', audit);
          console.log('Manually fetched auditor data:', audit.expand?.auditor);
        } catch (error) {
          console.error('Error fetching auditors:', error);
        }
      }

      // Default options if none provided
      const options: ReportOptions = {
        // Measure fields
        showIstzustand: true,
        showMassnahme: true,
        showBemerkung: false,
        showAnhange: true,
        
        // Audit info fields
        showDatum: true,
        showAuditoren: true,
        showDienststelle: true,
        showAdresse: true,
        showSicherheitssysteme: true,
        showBegleitpersonen: true,
        
        // Page settings
        orientation: 'landscape'
      };

       const previousAuditDate = previousAudit?.auditdatum || null;

       return { audit, massnahmen, settings, options, previousAuditDate };
    } catch (error) {
      console.error('Error fetching audit details:', error);
      throw new Error('Failed to fetch audit details');
    }
  }

  static async generateReport(auditId: string, options: ReportOptions): Promise<void> {
    try {
      const reportData = await this.fetchAuditDetails(auditId);
      // Override default options with provided options
      reportData.options = options;
      
      // Log auditor data for debugging (template handles missing data gracefully)
      if (!reportData.audit.expand?.auditor) {
        console.warn('Auditor data is missing for audit:', auditId);
      }
      
      // Generate HTML
      const htmlContent = await generateReportHTML(reportData);

      // Create modal container
      const modalContainer = document.createElement('div');
      modalContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        z-index: 9999;
      `;

      // Create toolbar
      const toolbar = document.createElement('div');
      toolbar.style.cssText = `
        background: #fff;
        padding: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      `;

      // Format date as DD-MM-YYYY
      const auditDate = new Date(reportData.audit.auditdatum);
      const formattedDate = `${auditDate.getDate().toString().padStart(2, '0')}-${(auditDate.getMonth() + 1).toString().padStart(2, '0')}-${auditDate.getFullYear()}`;
      const filename = `Audit_${reportData.audit.auditnummer}_${formattedDate}.pdf`;

      // Add buttons
      const printButton = document.createElement('button');
      printButton.innerHTML = '🖨️ Drucken';
      printButton.style.cssText = `
        padding: 8px 16px;
        margin-right: 8px;
        background: #007bff;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
      `;

      const closeButton = document.createElement('button');
      closeButton.innerHTML = '✕ Schließen';
      closeButton.style.cssText = `
        padding: 8px 16px;
        background: #6c757d;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
      `;

      toolbar.appendChild(printButton);
      toolbar.appendChild(closeButton);

      // Create iframe container
      const iframeContainer = document.createElement('div');
      iframeContainer.style.cssText = `
        flex: 1;
        position: relative;
        overflow: hidden;
        background: #f8f9fa;
      `;

      // Create and setup iframe
      const iframe = document.createElement('iframe');
      iframe.style.cssText = `
        width: 100%;
        height: 100%;
        border: none;
        background: white;
      `;

      iframeContainer.appendChild(iframe);
      modalContainer.appendChild(toolbar);
      modalContainer.appendChild(iframeContainer);
      document.body.appendChild(modalContainer);

      // Write content to iframe
      const iframeDoc = iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        iframeDoc.title = filename;
      }

      // Setup event listeners
      printButton.onclick = () => {
        iframe.contentWindow?.print();
      };

      closeButton.onclick = () => {
        document.body.removeChild(modalContainer);
      };

      // Close on background click
      modalContainer.onclick = (e) => {
        if (e.target === modalContainer) {
          document.body.removeChild(modalContainer);
        }
      };

      // Handle keyboard events
      document.addEventListener('keydown', function escapeHandler(e) {
        if (e.key === 'Escape') {
          document.body.removeChild(modalContainer);
          document.removeEventListener('keydown', escapeHandler);
        }
      });

      // Wait for images to load
      return new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  }
}
